/**
 * EPBCS — Hospitality Revenue Budgeting.
 * form="revenue_hosp_form1" → CY Forecast (Part A drivers + Part B revenue, 12 months).
 * form="revenue_hosp_form2" → Budget Year (PY + CY + BY, 36 months, using PY/CY ratios).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { PovBar, useDims, fmtMonth, fmtNum, ScenarioTh } from "./povUtils";
import { useCapexPov, capexPovFields } from "./CapexSection";
import { ActionButton, sectionForForm } from "./WorkflowActions";

export default function HospitalityGrid({ form }) {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [grid, setGrid] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!pov) return;
    setError(null);
    api.post("/epbcs/hospitality/grid", { form, pov })
      .then((r) => setGrid(r.data))
      .catch((e) => { setGrid(null); setError(e?.response?.data?.detail || e.message); });
  };
  useEffect(load, [pov, form]); // eslint-disable-line

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid={`hosp-error-${form}`}>{error}</p>;

  const canEdit = grid && (grid.perm === "input" || grid.perm === "full");

  const updCell = (code, m, v) => setGrid((g) => ({
    ...g, groups: g.groups.map((gr) => ({
      ...gr, rows: gr.rows.map((r) => (r.code === code ? { ...r, values: { ...r.values, [m]: v } } : r)),
    })),
  }));

  const commitCell = async (code, m, value) => {
    const v = value === "" ? 0 : +value;
    if (isNaN(v) || v < 0) {
      toast.error(`Value for ${fmtMonth(m)} must be a positive number`);
      load();
      return;
    }
    setSaving(true);
    try {
      await api.post("/epbcs/hospitality/line", { form, pov, code, month: m, value: v });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      load();
    } finally { setSaving(false); }
  };

  const seedDemo = async () => {
    setSaving(true);
    try {
      const r = await api.post("/epbcs/hospitality/seed-demo", { pov });
      toast.success(`Seeded ${r.data.seeded} demo values`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not seed demo data");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid={`hospitality-grid-${form}`}>
      <PovBar testid={`pov-${form}`} pov={pov} setPov={setPov} fields={capexPovFields(dims)}
        extra={<ActionButton section={sectionForForm(form)} pov={pov} onChanged={load} />} />

      {!grid ? <p className="text-sm text-slate-400">Loading grid…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100">
            <span className="text-[11px] text-slate-500">
              {form.startsWith("opex")
                ? "Occupancy & expense drivers shared with the revenue module — expense lines follow guest/cover-count/occupancy ratios."
                : ""}
              {form === "revenue_hosp_form1" || form === "opex_hosp_form1"
                ? " Actual months (grey) are entered directly; forecast months auto-calculate from each row's own driver formula."
                : " Only Budget Year months are shown — each row derives from PY/CY actual & forecast history using its own ratio rule."}
              {saving && <span className="ml-2 text-[#0B1E3F] font-semibold">Saving…</span>}
            </span>
            <span className="ml-auto flex items-center gap-2.5">
              {canEdit && (
                <button onClick={seedDemo} data-testid={`hosp-seed-demo-${form}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100">
                  <Sparkle size={11} weight="fill" /> Seed demo data
                </button>
              )}
              {!canEdit && <span className="text-[10px] font-bold uppercase text-slate-400">Read-only ({grid.perm})</span>}
            </span>
          </div>
          <div className="relative overflow-auto max-h-[calc(100vh-300px)]">
            <table className="text-[12px] w-full border-collapse">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.12em] uppercase text-slate-600">
                  <th className="py-2.5 px-3 min-w-[230px] sticky left-0 top-0 bg-[#F1F5F9] z-30 border-b border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                    {form.startsWith("opex") ? "Driver / Expense Line" : "Driver / Revenue Line"}
                  </th>
                  {grid.months.map((m) => (
                    <ScenarioTh key={m} m={m} scenarioByMonth={grid.scenario_by_month} testid={`scenario-badge-${form}-${m}`}
                      className="sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200" />
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <tr>
                      <td colSpan={1 + grid.months.length}
                        className="pt-3 pb-1 px-3 text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F] bg-slate-50 sticky left-0 z-10">
                        {g.label}
                      </td>
                    </tr>
                    {g.rows.map((row) => (
                      <HospRow key={row.code} row={row} grid={grid} canEdit={canEdit}
                        onLocal={updCell} onSave={commitCell} isTotal={g.key === "total" || g.key === "exp_total"} />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const HospRow = ({ row, grid, canEdit, onLocal, onSave, isTotal }) => (
  <tr className={`border-b border-slate-100 ${isTotal ? "bg-[#0B1E3F]/5 font-bold" : "odd:bg-white even:bg-[#F8FAFC]"} hover:bg-[#0B1E3F]/[0.03] transition-colors`}
    data-testid={`hosp-row-${row.code}`}>
    <td className="py-1.5 px-3 sticky left-0 bg-inherit z-10 border-r border-slate-200 whitespace-nowrap shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)]">
      {row.label}
      {row.kind === "derived" && (
        <span className="ml-1.5 px-1 rounded bg-[#0B1E3F]/5 text-[#0B1E3F] font-sans font-bold text-[9px] uppercase">calc</span>
      )}
    </td>
    {grid.months.map((m) => {
      const v = row.values[m];
      const editable = canEdit && (row.manual_months || []).includes(m);
      if (editable) {
        return (
          <td key={m} className="py-1 px-1">
            <input type="number" min="0" value={v ?? ""} data-testid={`hosp-cell-${row.code}-${m}`}
              onChange={(e) => onLocal(row.code, m, e.target.value)}
              onBlur={(e) => onSave(row.code, m, e.target.value)}
              className="w-[74px] border border-transparent hover:border-slate-300 bg-transparent px-1 py-0.5 text-[12px] text-right rounded-sm font-tabular-nums focus:border-[#C8A24B] focus:bg-[#FFFBEB] focus:ring-1 focus:ring-[#C8A24B] outline-none transition-all" />
          </td>
        );
      }
      const isActual = m <= grid.actuals_through;
      const display = row.unit === "ratio" ? (v || v === 0 ? Number(v).toFixed(2) : "—") : fmtNum(v);
      return (
        <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap font-tabular-nums ${isActual ? "text-slate-400 bg-slate-100/70" : "text-[#0B1E3F]"}`}>
          {display}
        </td>
      );
    })}
  </tr>
);
